import { useCallback, useEffect, useRef, useState } from "react";
import { Calculator, Edit3, Loader2, Pencil, RefreshCw, X } from "lucide-react";
import { api } from "../lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

/** Format euro amount in Italian locale: "€ 587,40" */
function formatEuro(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const formatted = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `€ ${formatted}`;
}

/** Parse user input "1.234,56" or "1234.56" → 1234.56 */
function parseEuro(text) {
  if (typeof text !== "string") return NaN;
  const cleaned = text
    .replace(/\s+/g, "")
    .replace(/€/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * R10 v0.5.8 — Totale offerta autocalcolato.
 *
 * - Watcher debounced (500ms) sui cambiamenti di `voci` (codici, qty, prezzi).
 * - Fetch `GET /api/quote/{id}/totals`.
 * - Override manuale (mostra valore custom + bottone "Riprendi auto").
 * - Tooltip mostra breakdown voci/tier.
 */
export default function TotaleOffertaCard({ sessionId, voci, session, onOverride }) {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);
  const lastSigRef = useRef("");

  // Build a small signature out of the voci so we don't fetch on no-op renders.
  const sig = JSON.stringify(
    (voci || []).map((v) => ({
      q: v.quantita,
      c: (v.candidati || []).map((c) => ({
        k: c.codice,
        p: c.prezzo,
        l: c.confidence_level,
        s: !!c.selezionato,
      })),
      cu: !!v._is_custom,
    }))
  );

  const fetchTotals = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await api.totals(sessionId);
      setTotals(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Debounced watcher: fetch totals 500ms after the last voce change.
  useEffect(() => {
    if (!sessionId) return;
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    const t = setTimeout(() => {
      fetchTotals();
    }, 500);
    return () => clearTimeout(t);
  }, [sig, sessionId, fetchTotals]);

  // Initial fetch when session opens
  useEffect(() => {
    if (!sessionId) return;
    fetchTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (!sessionId) return null;

  const isManual = !!totals?.totale_manual;
  const override = totals?.totale_override;
  const lordo = totals?.totale_lordo ?? 0;
  const displayed = isManual && typeof override === "number" ? override : lordo;
  const tier = totals?.voci_per_tier || {};
  const aListino = totals?.voci_a_listino ?? 0;
  const nonAListino = totals?.voci_non_a_listino ?? 0;
  const totalCount = totals?.voci_count ?? 0;

  const startEdit = () => {
    setDraftValue(
      typeof displayed === "number"
        ? displayed.toFixed(2).replace(".", ",")
        : ""
    );
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftValue("");
  };

  const saveOverride = async () => {
    const n = parseEuro(draftValue);
    if (Number.isNaN(n)) return;
    setSaving(true);
    try {
      await api.patchSession(sessionId, { totale_override: n });
      onOverride?.({ totale_override: n, totale_manual: true });
      await fetchTotals();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const resetAuto = async () => {
    setSaving(true);
    try {
      await api.patchSession(sessionId, { totale_manual: false });
      onOverride?.({ totale_override: null, totale_manual: false });
      await fetchTotals();
    } finally {
      setSaving(false);
    }
  };

  const tooltipText = `${aListino} voc${aListino === 1 ? "e" : "i"} a listino · ${nonAListino} non a listino${
    totalCount > 0
      ? `\nESATTO ${tier.ESATTO || 0} · PROSSIMO ${tier.PROSSIMO || 0} · VARIANTE ${tier.VARIANTE || 0}`
      : ""
  }`;

  return (
    <div data-testid="totale-offerta-card">
      <div className="flex items-center justify-between mb-2">
        <div className="tq-section-label flex items-center gap-1.5">
          <Calculator className="h-3 w-3" />
          Totale offerta
          {loading && (
            <Loader2
              className="h-3 w-3 animate-spin"
              style={{ color: "var(--text-muted)" }}
              data-testid="totale-loading"
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center justify-center h-6 w-6 rounded transition-colors"
              style={{ color: "var(--text-muted)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--bg-subtle)";
                e.currentTarget.style.color = "var(--text-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--text-muted)";
              }}
              data-testid="totale-edit-btn"
              aria-label="Modifica totale"
              title="Modifica totale"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {!editing && isManual && (
            <button
              type="button"
              onClick={resetAuto}
              className="inline-flex items-center justify-center h-6 w-6 rounded transition-colors"
              style={{ color: "var(--text-muted)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--bg-subtle)";
                e.currentTarget.style.color = "var(--text-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--text-muted)";
              }}
              data-testid="totale-reset-btn"
              aria-label="Riprendi calcolo automatico"
              title="Riprendi calcolo automatico"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="rounded-md p-3 cursor-help"
              style={{
                background: isManual ? "#FEF3C7" : "var(--bg-surface)",
                border: `1px solid ${isManual ? "#FDE68A" : "var(--border-subtle)"}`,
              }}
              data-testid="totale-display"
            >
              {editing ? (
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[14px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    €
                  </span>
                  <input
                    type="text"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveOverride();
                      if (e.key === "Escape") cancelEdit();
                    }}
                    autoFocus
                    placeholder="587,40"
                    className="flex-1 px-2 h-8 rounded text-[14px] tq-cell-mono"
                    style={{
                      border: "1px solid var(--border-default)",
                      background: "white",
                      color: "var(--text-primary)",
                    }}
                    data-testid="totale-input"
                  />
                  <button
                    type="button"
                    onClick={saveOverride}
                    disabled={saving || !draftValue.trim()}
                    className="inline-flex items-center justify-center h-8 px-2.5 rounded text-[12px] font-medium disabled:opacity-40"
                    style={{
                      background: "var(--accent-primary)",
                      color: "var(--text-on-accent)",
                    }}
                    data-testid="totale-save-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "OK"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="inline-flex items-center justify-center h-8 w-8 rounded"
                    style={{ color: "var(--text-muted)" }}
                    data-testid="totale-cancel-btn"
                    aria-label="Annulla"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div
                    className="text-[20px] font-semibold tq-cell-mono"
                    style={{
                      color: isManual ? "#92400E" : "var(--text-primary)",
                      fontFamily:
                        "JetBrains Mono, SF Mono, ui-monospace, monospace",
                    }}
                    data-testid="totale-value"
                  >
                    {formatEuro(displayed)}
                  </div>
                  {isManual && (
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                      style={{
                        background: "#92400E",
                        color: "white",
                      }}
                      data-testid="totale-manual-badge"
                    >
                      MANUALE
                    </span>
                  )}
                </div>
              )}

              {!editing && (
                <div
                  className="text-[11px] mt-1.5 flex items-center gap-1.5 flex-wrap"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span data-testid="totale-breakdown">
                    {aListino} voc{aListino === 1 ? "e" : "i"} a listino
                  </span>
                  {nonAListino > 0 && (
                    <>
                      <span>·</span>
                      <span style={{ color: "#B45309" }}>
                        {nonAListino} non a listino
                      </span>
                    </>
                  )}
                  {isManual && lordo !== displayed && (
                    <>
                      <span>·</span>
                      <span data-testid="totale-auto-hint">
                        auto: {formatEuro(lordo)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent
            className="max-w-[280px] text-[11px] leading-snug whitespace-pre-line"
            style={{ background: "#0f172a", color: "white" }}
            side="left"
            sideOffset={6}
          >
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
